
import { throwError as observableThrowError, Observable, throwError } from 'rxjs';

import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { BaseEntityService, BaseEntity, blockUi } from "@bndes/bndes-ux4/bndes-angular-utils";
import { BnAlertsService } from "@bndes/bndes-ux4/bn-alerts";

import { OcorrenciaValidacaoVO } from './ocorrencia-validacao.vo';
import { msgErroClientSide, msgErroUrlRest } from '../constantes/validacao.constantes';
import { PaginacaoBackUtilsService } from './paginacao-back-utils.service';
import { useCache } from "@bndes/bndes-ux4/bndes-angular-utils";
import { MENSAGENS_ERRO } from './fgi-mensagens-erro.model.';


@Injectable()
export abstract class FgiBackEndService<T extends BaseEntity> extends BaseEntityService<T> {

  public ocorrencias: OcorrenciaValidacaoVO[] = [];
  mensagensErro = MENSAGENS_ERRO[0];

  constructor(
    protected http: HttpClient,
    private paginacaoBackUtils: PaginacaoBackUtilsService,
    private bnAlertsService: BnAlertsService,
    protected router: Router) {
    super();
  }

  // Fazer override de todos os métodos de BaseEntityService que for usar
  // chamando o método da classe mãe e adicionando tratamento de erros
  // e alertas

  obterNumeroPaginaBack(paginaFront: number, tamanhoPaginaFront: number) {
    return this.paginacaoBackUtils.obterNumeroPaginaBack(paginaFront, tamanhoPaginaFront);
  }

  obterTamanhoPaginaBack() {
    return this.paginacaoBackUtils.tamanhoPagina;
  }

  findById(id: number, options?: {}): Observable<T> {
    return super.findById(id, options).pipe(
      catchError(e => this.handleError(e))) as Observable<T>;
  }

  find(params?: any, options?: any): Observable<T[]> {
    return super.find(params, options)
      .pipe(catchError(e => this.handleError(e))) as Observable<T[]>;
  }


  save(entity: T, options?: any): Observable<T> {
    return super.save(entity, options).pipe(
      catchError((e) => this.handleError(e))) as Observable<T>;
  }

  update(entity: T, options?: any): Observable<T> {
    return this.http.put(`${this.BASE_URL}/${entity.id}`, entity, options).pipe(
      map(response => this.convertToType(response)),
      catchError((e) => this.handleError(e))) as Observable<T>;
    // reescrevendo completamente - o update do bndes angular utils usa um POST
    // return super.update(entity, options).catch((e) => this.handleError(e));
  }

  // usar 'desativa' em vez de 'remove' quando for exclusão logica
  desativa(id: number, options?: any): Observable<T> {
    return super.remove(id, options).pipe(
      map(response => this.convertToType(response)),
      catchError((e) => this.handleError(e))) as Observable<T>;
  }

  remove(id: number, options?: any): Observable<ArrayBuffer> {
    return super.remove(id, options).pipe(
      catchError((e) => this.handleError(e))) as Observable<ArrayBuffer>;
  }


  protected handleError(error: Response | any) {
    if (error.status) {
      this.ocorrencias = [];
      switch (error.status) {
        case 210:
        case 400:
        case 404:
        case 500:
          this.ocorrencias = error.error;
          if (this.ocorrencias) {
            if (this.ocorrencias.length == 1 && this.ocorrencias[0].mensagem.indexOf("Parâmetro inválido (campos inválidos)") !== -1) {
              let ocorrencia = new OcorrenciaValidacaoVO("A operação não conseguiu ter críticas avançadas realizadas, pode haver outros erros.", "E");
              this.ocorrencias.push(ocorrencia);
            }
            this.ocorrencias = this.ocorrencias.map(ocorrencia => Object.assign(new OcorrenciaValidacaoVO('', '', ''), ocorrencia));
          } else {
            this.ocorrencias = [];
            if (error.status === 400) {
              this.criaOcorrenciaClientSide("Existem campos obrigatórios que não foram preenchidos");
            }
            else {
              console.log(error.message);
              this.criaOcorrenciaClientSide(error.message);
            }
          }
          break;
        case 401:
          this.router.navigate(['/login']);
          break;
        case 403:
            let erro = error.error;
            if(erro && error.error[0].mensagem){
              let mensagemErro = this.trataMensagemErro(error.error[0].mensagem);
              this.criaOcorrenciaClientSide(mensagemErro);
            } else {
                this.criaOcorrenciaClientSide('Usuário sem permissão para realizar esta ação');
            }
            break;
        case 405:
            this.criaOcorrenciaClientSide(msgErroUrlRest + error.url);
            break;
        case 413:
          this.criaOcorrenciaClientSide('Um componente de rede rejeitou a solicitação por ser muito grande. Verifique se ela foi processada.');
          break;
        case 504:
          this.criaOcorrenciaClientSide(msgErroClientSide);
      }
    } else {
      // this.criaOcorrenciaClientSide(msgErroClientSide);
    }

    this.ocorrencias.forEach(ocorrencia => {
      this.bnAlertsService.criarAlerta(ocorrencia.getToastType(), '', ocorrencia.mensagem, 10000); 
    });

    return throwError(new Error(error.status));
  }

  // HandleError sem o uso de alertas
  protected handleErrorLote(error: Response | any) {
    if (error.status) {
      switch (error.status) {
        case 400:
        case 403:
        case 404:
        case 500:
          this.ocorrencias = error.error;
          this.ocorrencias = this.ocorrencias.map(
            ocorrencia => Object.assign(new OcorrenciaValidacaoVO('', '', ''), ocorrencia));
          console.log(this.ocorrencias);
          break;
        case 405:
          this.criaOcorrenciaClientSide(msgErroUrlRest + error.url);
          break;
        case 401:
          this.router.navigate(['/login']);
          break;
        case 504:
          this.criaOcorrenciaClientSide(msgErroClientSide);
      }
    } else {
      this.criaOcorrenciaClientSide(msgErroClientSide);
    }

    return observableThrowError(new Error(error.status));
  }

  protected criaOcorrenciaClientSide(msg: string): void {
    this.ocorrencias.push(new OcorrenciaValidacaoVO(msg, 'F'));
  }

  
  getTimeStampISO() {
    return this.http.get(`rest/front/data-sistema/ISO`, useCache()).pipe(
      catchError((e) => this.handleError(e))) as Observable<JSON>;
  }

  trataMensagemErro(mensagemErro: string): string{
    let mensagemRetornada = '';

    if (mensagemErro.includes("Usuário não possui acesso a esta modalidade")) {
      mensagemRetornada = this.semPermissaoModalidadesDiferentes();
    } 
    else if (mensagemErro.includes("encontra") && mensagemErro.includes("ide")) {
      mensagemRetornada = mensagemErro;
    }
    else {
      mensagemRetornada = this.mensagensErro.semPermissaoPadrao;
    } 
   
    return mensagemRetornada;
  }

  semPermissaoModalidadesDiferentes(): string {
    let retorno = '';
    let url = window.location.href;
    
    if(url.includes("lote")){
      retorno = this.mensagensErro.semPermissaoLote;
    } else {
     retorno = this.semPermissaoDetalhe(url);
    }
   
    return retorno;
  }

  semPermissaoDetalhe(url: string){
    if (url.includes("operacoes")) {
      return this.mensagensErro.opeCredNaoEncontrada;
    } else if (url.includes("recuperacoes")) {
      return this.mensagensErro.rcNaoEncontrada;
    } else if (url.includes("solicitacoes")) {
      return this.mensagensErro.solHonraNaoEncontrada;
    } else {
      return this.mensagensErro.infoNaoEncontrada;
    }
  }
}
